import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@mui/material';
import CallSplitIcon from 'mdi-react/CallSplitIcon';
import type { MultiPolygonCoordinates } from '../models/editor-feature';

interface Props {
  readonly coordinates: MultiPolygonCoordinates;
  readonly onSplit: () => void;
}

/**
 * Says when the selected shape is really several shapes, and offers to separate them.
 *
 * Some stored plans hold a whole floor's rooms as one feature with thirteen polygons in it.
 * Clicking any of those rooms selects all thirteen and draws a selection box across the entire
 * floor, so the room cannot be moved, resized, reshaped or named — and nothing on screen says
 * why. The shape looks like thirteen rooms and behaves like one object.
 *
 * Offered rather than done on load: splitting changes what gets saved, and a plan that
 * silently gains twelve features is a change nobody asked for and cannot see. Shown only when
 * there is something to split, so it is absent on every plan that does not have the problem.
 */
const SplitPartsNotice = ({ coordinates, onSplit }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();
  const parts = coordinates.length;

  if (parts < 2) {
    return null;
  }

  return (
    <Alert
      severity="info"
      sx={{ py: 0, '& .MuiAlert-message': { py: 1 } }}
      action={(
        <Button
          size="small"
          startIcon={<CallSplitIcon size={14} />}
          onClick={onSplit}
          sx={{ textTransform: 'none' }}
        >
          {t('floorplan-editor-split-action')}
        </Button>
      )}
    >
      {t('floorplan-editor-split-notice', { count: parts })}
    </Alert>
  );
};

export default SplitPartsNotice;
